const storageAvailable = (type: 'localStorage' | 'sessionStorage') => {
  let storage;
  try {
    storage = window[type];
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof window.DOMException &&
      // chrome when site data blocked in settings
      (e.code === 11 ||
        // everything except Firefox
        e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      !!(storage && storage.length !== 0)
    );
  }
};

export const localStorageAvailable = (): boolean =>
  storageAvailable('localStorage');

/**
 * Set local storage - includes error handling and returns false / true
 * if operation is successful
 *
 * @param key - local storage key to get.
 * @param value - default value if retrieval unsuccesful.
 * @param stringify - JSON parse the value after getting.  Default: true
 */
export const getLocalStorage = <T, D>(
  key: string,
  defaultVal: D,
  parse = true,
) => {
  if (!localStorageAvailable()) {
    return defaultVal;
  }
  try {
    const storageData = window.localStorage.getItem(key);
    if (parse && storageData) {
      const data = JSON.parse(storageData);
      return data as T;
    }
    if (storageData) {
      return storageData as unknown as T;
    }
    return defaultVal;
  } catch (err) {
    return defaultVal;
  }
};

/**
 * Remove local storage value for a key.
 * @param key - key to remove from local storage
 */
export const clearLocalStorage = (key: string): boolean => {
  if (!localStorageAvailable()) {
    return false;
  }
  window.localStorage.removeItem(key);
  return true;
};

/**
 * Set local storage - includes error handling and returns false / true
 * if operation is successful
 *
 * @param key - local storage key to set.
 * @param value - value to set.
 * @param stringify - stringify the value before setting.  Default: true
 */
export const setLocalStorage = (
  key: string,
  value: string | Record<string, any> | number | boolean,
  stringify = true,
): boolean => {
  if (!localStorageAvailable()) {
    return false;
  }
  try {
    let finalValue = value as string;
    if (stringify || typeof value === 'object') {
      finalValue = JSON.stringify(value);
    }
    window.localStorage.setItem(key, finalValue);
    return true;
  } catch (err) {
    return false;
  }
};

/**
 * Safely loop through local storage keys applying callback function to each.
 * Returns boolean indicating whether operation succeeded or not.
 * @param callback
 */
export const iterLocalStorageKeys = (
  callback: (key: string) => void,
): boolean => {
  if (!localStorageAvailable()) {
    return false;
  }

  try {
    Object.keys(localStorage).forEach((key) => {
      callback(key);
    });
    return true;
  } catch {
    return false;
  }
};
